import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, Key, MutableRefObject, ReactNode, Ref } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { ChevronDown, ChevronUp, Table2 } from 'lucide-react';
import { useListViewTableScroll } from '../../hooks/useListViewTableScroll';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { readAppliedUiZoom } from '../../config/zoomBreakpoints';
import { IpcEmpty } from './IpcEmpty';
import { IpcPagination } from './IpcPagination';
import { IpcTablePanel } from './IpcTablePanel';

export type IpcTablePaginationConfig = {
  current: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  onChange: (page: number, pageSize: number) => void;
  className?: string;
  hideOnSinglePage?: boolean;
};

function readColumnSortValue<T extends object>(
  record: T,
  dataIndex: ColumnType<T>['dataIndex'],
  key?: Key,
): unknown {
  if (dataIndex != null) {
    if (Array.isArray(dataIndex)) {
      let cur: unknown = record;
      for (const part of dataIndex) {
        if (cur == null || typeof cur !== 'object') return undefined;
        cur = (cur as Record<string | number, unknown>)[part as string | number];
      }
      return cur;
    }
    return (record as Record<string | number, unknown>)[dataIndex as string | number];
  }
  if (key != null && (typeof key === 'string' || typeof key === 'number')) {
    return (record as Record<string | number, unknown>)[key];
  }
  return undefined;
}

function compareColumnSortValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null || a === '') return 1;
  if (b == null || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') {
    return Number.isNaN(a) && Number.isNaN(b) ? 0 : Number.isNaN(a) ? 1 : Number.isNaN(b) ? -1 : a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);

  const as = String(a).trim();
  const bs = String(b).trim();
  const dateLike = /^\d{4}[-/]\d{1,2}([-/\s]\d{1,2})?/;
  if (dateLike.test(as) && dateLike.test(bs)) {
    const da = Date.parse(as);
    const db = Date.parse(bs);
    if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
  }

  const na = Number(as);
  const nb = Number(bs);
  if (as !== '' && bs !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;

  return as.localeCompare(bs, 'zh-CN', { numeric: true, sensitivity: 'base' });
}

/** 未排序不显示图标；升/降序才显示，取消排序后消失 */
function ipcDefaultSortIcon({ sortOrder }: { sortOrder: SortOrder }) {
  if (sortOrder === 'ascend') {
    return (
      <span className="ipc-table-sort-icon" aria-hidden>
        <ChevronUp size={12} strokeWidth={2.25} />
      </span>
    );
  }
  if (sortOrder === 'descend') {
    return (
      <span className="ipc-table-sort-icon" aria-hidden>
        <ChevronDown size={12} strokeWidth={2.25} />
      </span>
    );
  }
  return null;
}

/** 默认给未声明 sorter 的列补前端排序；显式 sorter / sorter:false 原样保留 */
function withDefaultColumnSorters<T extends object>(
  columns: ColumnsType<T> | undefined,
  enabled: boolean,
): ColumnsType<T> | undefined {
  if (!enabled || columns == null) return columns;

  return columns.map(column => {
    if ('children' in column && Array.isArray(column.children) && column.children.length > 0) {
      return {
        ...column,
        children: withDefaultColumnSorters(column.children as ColumnsType<T>, true),
      };
    }

    const leaf = column as ColumnType<T>;
    const next: ColumnType<T> = { ...leaf };

    if (!Object.prototype.hasOwnProperty.call(leaf, 'sorter')) {
      const { dataIndex, key } = leaf;
      next.sorter = (a: T, b: T) =>
        compareColumnSortValues(
          readColumnSortValue(a, dataIndex, key),
          readColumnSortValue(b, dataIndex, key),
        );
      next.showSorterTooltip = leaf.showSorterTooltip ?? false;
    }

    if (next.sorter && next.sorter !== false) {
      if (next.sortIcon == null) {
        next.sortIcon = ipcDefaultSortIcon;
      }
      if (next.showSorterTooltip === undefined) {
        next.showSorterTooltip = false;
      }
    }

    return next;
  });
}

/**
 * IpcTable：统一上下 padding 8px、内容区 line-height 22px；多行同 padding 自动增高。
 * className 常用：`ipc-table-preline`（允许换行）、`ipc-table--dense`（高密度例外）。
 * 默认对当前 dataSource（一般为当前页）做前端列排序；`sortable={false}` 可关闭。
 */
export type IpcTableProps<T extends object = object> = Omit<TableProps<T>, 'pagination' | 'title'> & {
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  headExtra?: ReactNode;
  panelClassName?: string;
  bodyClassName?: string;
  scrollClassName?: string;
  scrollRef?: Ref<HTMLDivElement>;
  compact?: boolean;
  frameless?: boolean;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyText?: ReactNode;
  pagination?: false | IpcTablePaginationConfig;
  selectedCount?: number;
  showHeaderIcon?: boolean;
  /** 默认 true：未声明 sorter 的列自动支持当前页前端排序 */
  sortable?: boolean;
  resizable?: boolean;
  resizableMinWidth?: number;
  /** 受控列宽（如规格矩阵表按版本持久化） */
  resizableWidths?: Record<string, number>;
  onResizableWidthsChange?: (widths: Record<string, number>) => void;
  onResizableWidthCommit?: (columnKey: string, width: number) => void;
  isColumnResizable?: (columnKey: string) => boolean;
  bodyHeightOffset?: number;
  stableBodyHeight?: boolean;
  autoHeight?: boolean;
  autoHeightBottomGap?: number;
  autoHeightMinBodyHeight?: number;
  autoHeightDeps?: ReadonlyArray<unknown>;
  /** 页面列表传入 LIST_VIEW_KEYS 对应值，恢复表体滚动；弹框列表不要传。 */
  listViewKey?: string;
};

export function IpcTable<T extends object = object>({
  title,
  subtitle,
  icon,
  headExtra,
  panelClassName,
  bodyClassName,
  scrollClassName,
  scrollRef,
  compact = false,
  frameless = false,
  emptyTitle,
  emptyDescription,
  emptyText,
  pagination,
  selectedCount,
  showHeaderIcon = true,
  sortable = true,
  className,
  locale,
  rowSelection,
  columns,
  components,
  tableLayout,
  scroll,
  resizable = false,
  resizableMinWidth,
  resizableWidths,
  onResizableWidthsChange,
  onResizableWidthCommit,
  isColumnResizable,
  bodyHeightOffset = 10,
  stableBodyHeight = true,
  autoHeight = false,
  autoHeightBottomGap = 16,
  autoHeightMinBodyHeight = 120,
  autoHeightDeps = [],
  listViewKey,
  ...tableProps
}: IpcTableProps<T>) {
  const shellRef = useRef<HTMLDivElement>(null);
  const enhancedColumns = useMemo(
    () => withDefaultColumnSorters(columns, sortable),
    [columns, sortable],
  );
  const resizableTable = useResizableColumns<T>(enhancedColumns ?? [], {
    minWidth: resizableMinWidth,
    controlledWidths: resizableWidths,
    onControlledWidthsChange: onResizableWidthsChange,
    onResizeCommit: onResizableWidthCommit,
    isResizable: isColumnResizable,
  });
  const [autoScrollY, setAutoScrollY] = useState<number | undefined>(undefined);
  const tableLoading = Boolean(tableProps.loading);
  const dataLength = Array.isArray(tableProps.dataSource) ? tableProps.dataSource.length : 0;
  const paginationConfig = pagination === false ? undefined : pagination;
  useListViewTableScroll(listViewKey, shellRef, {
    ready: !tableLoading && dataLength > 0 && (!autoHeight || autoScrollY != null),
    page: paginationConfig?.current ?? 1,
    pageSize: paginationConfig?.pageSize ?? 20,
    layoutKey: `${autoScrollY ?? ''}:${dataLength}:${tableLoading ? 1 : 0}`,
  });
  const rowSelectionKeys = rowSelection && 'selectedRowKeys' in rowSelection
    ? rowSelection.selectedRowKeys as Key[] | undefined
    : undefined;
  const finalSelectedCount = selectedCount ?? rowSelectionKeys?.length;
  const shouldShowPagination = paginationConfig != null
    && (!paginationConfig.hideOnSinglePage || paginationConfig.total > paginationConfig.pageSize);
  const measuredScroll = resizable ? resizableTable.getScroll(scroll) : scroll;
  const finalScroll = autoHeight
    ? {
        ...(measuredScroll ?? {}),
        ...(autoScrollY != null ? { y: autoScrollY } : {}),
      }
    : measuredScroll;
  const fixedBodyHeight = finalScroll
    && typeof finalScroll === 'object'
    && 'y' in finalScroll
    && finalScroll.y != null
    && finalScroll.y !== true
      ? finalScroll.y
      : undefined;
  const shouldFixBodyHeight = (autoHeight || stableBodyHeight) && fixedBodyHeight != null;
  const finalBodyHeightOffset = autoHeight ? 0 : bodyHeightOffset;
  const shellStyle = shouldFixBodyHeight
    ? ({
        '--ipc-table-body-height': typeof fixedBodyHeight === 'number'
          ? `${fixedBodyHeight + finalBodyHeightOffset}px`
          : finalBodyHeightOffset === 0
            ? String(fixedBodyHeight)
            : `calc(${fixedBodyHeight} + ${finalBodyHeightOffset}px)`,
      } as CSSProperties)
    : undefined;
  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    if (typeof scrollRef === 'function') {
      scrollRef(node);
    } else if (scrollRef && typeof scrollRef === 'object') {
      (scrollRef as MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [scrollRef]);
  const tableClassName = [
    'ipc-table',
    resizable ? resizableTable.tableClassName : '',
    className,
  ].filter(Boolean).join(' ');
  const tableComponents = resizable
    ? {
        ...resizableTable.components,
        ...components,
        header: {
          ...resizableTable.components?.header,
          ...components?.header,
        },
        ...(components?.body ? { body: components.body } : {}),
      }
    : components;
  const paginationNode = shouldShowPagination && paginationConfig ? (
    <IpcPagination
      className={paginationConfig.className}
      current={paginationConfig.current}
      pageSize={paginationConfig.pageSize}
      total={paginationConfig.total}
      pageSizeOptions={paginationConfig.pageSizeOptions}
      selectedCount={finalSelectedCount}
      onChange={paginationConfig.onChange}
    />
  ) : null;

  useLayoutEffect(() => {
    if (!autoHeight) {
      setAutoScrollY(undefined);
      return undefined;
    }

    const shell = shellRef.current;
    if (!shell) return undefined;

    let frame = 0;
    const readHeight = (selector: string, fallback = 0) => {
      const el = shell.querySelector(selector);
      if (!(el instanceof HTMLElement)) return fallback;
      return el.getBoundingClientRect().height || fallback;
    };

    const getPaginationHeight = () => {
      const paginationEl = Array.from(shell.children).find(
        child => child instanceof HTMLElement && child.classList.contains('ipc-pagination'),
      ) as HTMLElement | undefined;
      if (!paginationEl) return 0;
      const styles = window.getComputedStyle(paginationEl);
      const marginTop = Number.parseFloat(styles.marginTop) || 0;
      const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
      return paginationEl.getBoundingClientRect().height + marginTop + marginBottom;
    };

    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const rect = shell.getBoundingClientRect();
        // zoom 高度补偿后 documentElement.clientHeight 可能是布局放大值；用视觉视口 innerHeight
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const panelHeaderHeight = readHeight('.ipc-table-panel__head');
        const tableHeaderHeight =
          readHeight('.ant-table-header') ||
          readHeight('.ant-table-thead') ||
          40;
        const paginationHeight = shouldShowPagination ? getPaginationHeight() : 0;
        // getBoundingClientRect / innerHeight 是可视像素；scroll.y 是布局像素，zoom<1 时需 /zoom，否则表体偏矮留白
        const zoom = readAppliedUiZoom();
        const availableVisual = viewportHeight
          - rect.top
          - autoHeightBottomGap
          - paginationHeight
          - panelHeaderHeight
          - tableHeaderHeight;
        const available = availableVisual / zoom;
        const nextHeight = available >= autoHeightMinBodyHeight
          ? Math.floor(available)
          : Math.max(48, Math.floor(available));

        setAutoScrollY(prev => (prev === nextHeight ? prev : nextHeight));
      });
    };

    measure();
    window.addEventListener('resize', measure);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(shell);
    if (shell.parentElement) observer?.observe(shell.parentElement);
    const viewport = shell.closest('.ipc-table-viewport');
    if (viewport instanceof HTMLElement) observer?.observe(viewport);

    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(shell, { childList: true, subtree: true, attributes: true });

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      mutationObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass autoHeightDeps for data/layout changes
  }, [autoHeight, autoHeightBottomGap, autoHeightMinBodyHeight, shouldShowPagination, title, subtitle, headExtra, ...autoHeightDeps]);

  return (
    <div
      ref={shellRef}
      className={[
        'ipc-table-shell',
        shouldShowPagination ? 'ipc-table-shell--with-pagination' : '',
        shouldFixBodyHeight ? 'ipc-table-shell--fixed-body' : '',
        autoHeight ? 'ipc-table-shell--auto-height' : '',
      ].filter(Boolean).join(' ')}
      style={shellStyle}
    >
      <IpcTablePanel
        title={title}
        subtitle={subtitle}
        icon={icon ?? (showHeaderIcon && title != null ? <Table2 size={16} /> : null)}
        headExtra={headExtra}
        className={panelClassName}
        bodyClassName={bodyClassName}
        scrollClassName={scrollClassName}
        scrollRef={setScrollRef}
        compact={compact}
        frameless={frameless}
      >
        <Table<T>
          {...tableProps}
          columns={resizable ? resizableTable.columns : enhancedColumns}
          components={tableComponents}
          tableLayout={resizable ? resizableTable.tableLayout : tableLayout}
          scroll={finalScroll}
          rowSelection={rowSelection}
          className={tableClassName}
          pagination={false}
          locale={{
            emptyText: emptyText ?? <IpcEmpty title={emptyTitle} description={emptyDescription} />,
            ...locale,
          }}
        />
      </IpcTablePanel>
      {paginationNode}
    </div>
  );
}
